/**
 * Kong transitions: concealed (own turn), exposed (claimed off a discard),
 * and added (upgrading an existing exposed Pung from a self-drawn tile).
 * Split out of `claimTransitions.ts` for the 200-line cap; re-exported from
 * `GameState.ts` as the public API. Pure — each returns a NEW state.
 *
 * Every kong resolves with an immediate replacement draw from `deadWall`
 * (`drawKongReplacement`, the dead-wall sibling of `claimTransitions.ts`'s
 * `drawForSeat`). Added kong is the one exception: it OPENS a Ron-only
 * robbing-the-kong claim window first (`declareAddedKong`) and only actually
 * melds + draws a replacement once that window closes with no robber
 * (`resolveAddedKong`, called from `claimTransitions.ts`'s `passClaims`).
 */

import { isSameKind, sortTiles, type Meld, type Seat, type TileKind } from './types.js';
import {
  addedKongAvailable,
  canExposedKong,
  concealedKongOptions,
  KONG_MATCH_NEEDED,
} from './Claims.js';
import { applyClaim, requireDiscard, requirePhase, type MahjongState } from './stateCore.js';

/**
 * Draws the front of the dead wall for `seat`; an empty dead wall ends the
 * hand as exhausted. Always clears `pendingAddedKongSeat` — this is the
 * generic "resolve back to awaiting-discard" step, and any robbing-the-kong
 * window that led here is by definition over.
 */
function drawKongReplacement(state: MahjongState, seat: Seat): MahjongState {
  if (state.deadWall.length === 0) {
    return {
      ...state,
      phase: 'hand-end',
      lastDiscard: null,
      drawnTile: null,
      result: { kind: 'exhausted' },
      pendingAddedKongSeat: null,
    };
  }
  const [drawn, ...rest] = state.deadWall;
  if (drawn === undefined) throw new Error('unreachable: non-empty dead wall yielded no tile');
  return {
    ...state,
    hands: state.hands.map((h, i) => (i === seat ? [...h, drawn] : h)),
    deadWall: rest,
    turn: seat,
    phase: 'awaiting-discard',
    lastDiscard: null,
    drawnTile: drawn,
    pendingAddedKongSeat: null,
  };
}

/** A seat declares a self-drawn Kong from four matching concealed tiles, on their own turn. */
export function declareConcealedKong(
  state: MahjongState,
  seat: Seat,
  kind: TileKind,
): MahjongState {
  requirePhase(state, 'awaiting-discard');
  if (seat !== state.turn) throw new Error(`seat ${seat} cannot kong out of turn`);
  const hand = state.hands[seat] ?? [];
  const options = concealedKongOptions(hand, state.ruleset);
  if (!options.some((option) => isSameKind(option, kind))) {
    throw new Error(`seat ${seat} cannot declare a concealed kong of this kind`);
  }
  const matching = hand.filter((t) => isSameKind(t, kind));
  const meld: Meld = { type: 'kong', tiles: sortTiles(matching), concealed: true };
  const usedIds = new Set(matching.map((t) => t.id));
  const afterMeld: MahjongState = {
    ...state,
    hands: state.hands.map((h, i) => (i === seat ? h.filter((t) => !usedIds.has(t.id)) : h)),
    melds: state.melds.map((m, i) => (i === seat ? [...m, meld] : m)),
  };
  return drawKongReplacement(afterMeld, seat);
}

/** A seat claims an exposed Kong off the open discard (three matching concealed tiles + the discard). */
export function claimExposedKong(state: MahjongState, seat: Seat): MahjongState {
  requirePhase(state, 'claim-window');
  const discard = requireDiscard(state);
  if (seat === discard.from) throw new Error('the discarder cannot kong their own tile');
  const hand = state.hands[seat] ?? [];
  if (!canExposedKong(hand, discard.tile, state.ruleset)) {
    throw new Error(`seat ${seat} cannot kong this discard`);
  }
  const triple = hand.filter((t) => isSameKind(t, discard.tile)).slice(0, KONG_MATCH_NEEDED);
  const meld: Meld = { type: 'kong', tiles: sortTiles([...triple, discard.tile]), concealed: false };
  const usedIds = new Set(triple.map((t) => t.id));
  const afterMeld = applyClaim(state, seat, discard.from, discard.tile.id, meld, usedIds);
  return drawKongReplacement(afterMeld, seat);
}

/**
 * A seat declares that it will upgrade an existing exposed Pung into a Kong
 * using a self-drawn matching tile. This does NOT resolve the kong — it
 * opens a Ron-only robbing-the-kong claim window against the tile, reusing
 * the exact shape a real discard uses (`lastDiscard`) so the existing
 * `declareRon` works unmodified for robbing. The tile stays in the seat's
 * hand and the pung meld is untouched until the window resolves: either a
 * robber wins via Ron (the kong never happens, nothing to undo), or nobody
 * robs and `resolveAddedKong` finalizes the meld upgrade + replacement draw.
 */
export function declareAddedKong(state: MahjongState, seat: Seat, tileId: number): MahjongState {
  requirePhase(state, 'awaiting-discard');
  if (seat !== state.turn) throw new Error(`seat ${seat} cannot kong out of turn`);
  const hand = state.hands[seat] ?? [];
  const tile = hand.find((t) => t.id === tileId);
  if (tile === undefined) throw new Error(`tile ${tileId} not in seat ${seat}'s hand`);
  const melds = state.melds[seat] ?? [];
  if (!addedKongAvailable(melds, tile, state.ruleset)) {
    throw new Error(`seat ${seat} cannot add this tile to an exposed pung`);
  }
  return {
    ...state,
    phase: 'claim-window',
    lastDiscard: { tile, from: seat },
    pendingAddedKongSeat: seat,
  };
}

/**
 * Finalizes an added-kong declaration once its robbing-the-kong window
 * closes with nobody claiming Ron: upgrades the pung meld to a kong and
 * draws the replacement from the dead wall, exactly like the old
 * immediate-resolution `declareAddedKong` used to.
 */
export function resolveAddedKong(state: MahjongState): MahjongState {
  if (state.pendingAddedKongSeat === null) {
    throw new Error('resolveAddedKong called with no robbing-the-kong window open');
  }
  const seat = state.pendingAddedKongSeat;
  const discard = requireDiscard(state);
  const tile = discard.tile;
  const melds = state.melds[seat] ?? [];
  const pungMeld = melds.find(
    (meld) => meld.type === 'pung' && meld.tiles.some((t) => isSameKind(t, tile)),
  );
  if (pungMeld === undefined) throw new Error('unreachable: added-kong window with no matching pung');
  const upgraded: Meld = { type: 'kong', tiles: sortTiles([...pungMeld.tiles, tile]), concealed: false };
  const afterMeld: MahjongState = {
    ...state,
    hands: state.hands.map((h, i) => (i === seat ? h.filter((t) => t.id !== tile.id) : h)),
    melds: state.melds.map((m, i) =>
      i === seat ? m.map((meld) => (meld === pungMeld ? upgraded : meld)) : m,
    ),
  };
  return drawKongReplacement(afterMeld, seat);
}
